/**
 * BasinGaugesStrip — hottest HII water-level gauges near Sikhio.
 * Map pins alone are easy to miss; this strip puts freeboard on the rail.
 */

import type { BasinStation, FallbackTier } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";

interface Props {
  stations: BasinStation[];
  loading?: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
}

const SIT_COLOR: Record<number, string> = {
  1: "var(--bad)",
  2: "var(--warn)",
  3: "var(--good)",
  4: "var(--warn)",
  5: "var(--bad)",
};

export function BasinGaugesStrip({ stations, loading, ageMinutes, fallbackTier }: Props) {
  const hot = stations
    .filter((s) => s.kind === "waterlevel" && s.situationLevel != null)
    .slice()
    .sort((a, b) => {
      const sl = (b.situationLevel ?? 0) - (a.situationLevel ?? 0);
      if (sl !== 0) return sl;
      return (a.freeboardM ?? 99) - (b.freeboardM ?? 99);
    })
    .slice(0, 4);

  if (loading && hot.length === 0) {
    return (
      <div className="col">
        <div className="eyebrow">สถานีวัดน้ำ · BASIN</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 12, marginTop: 6, width: "65%" }} />
      </div>
    );
  }

  if (hot.length === 0) {
    return (
      <div className="col" style={{ gap: 4 }}>
        <PanelHeader
          title="สถานีวัดน้ำ "
          fallbackTier={fallbackTier}
          ageMinutes={ageMinutes}
          source="thaiwater.hii"
        />
        <div className="caption mono" style={{ color: "var(--warn)" }} role="status">
          ◆ FEED GAP · no water-level gauges in range
        </div>
      </div>
    );
  }

  return (
    <div className="col" style={{ gap: 6 }}>
      <PanelHeader
        title="สถานีวัดน้ำ "
        fallbackTier={fallbackTier}
        ageMinutes={ageMinutes}
        source="thaiwater.hii"
      />
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        {hot.map((s) => {
          const level = s.situationLevel ?? 0;
          const col = SIT_COLOR[level] ?? "var(--data)";
          return (
            <li
              key={s.id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: "2px 10px",
                borderLeft: `3px solid ${col}`,
                paddingLeft: 8,
              }}
            >
              <span className="caption" style={{ fontWeight: 600 }}>{s.name}</span>
              <span className="mono caption" style={{ color: col, textAlign: "right" }}>
                L{level}/5
              </span>
              <span className="caption mono" style={{ color: "var(--text-3)" }}>
                {s.distanceKm.toFixed(0)} กม.
                {s.freeboardM != null ? ` · freeboard ${s.freeboardM.toFixed(2)} ม.` : ""}
              </span>
              <span className="mono caption" style={{ color: "var(--text-3)", textAlign: "right" }}>
                {s.waterlevelMsl != null ? `${s.waterlevelMsl.toFixed(2)} ม.รทก.` : "—"}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
